import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import {
  MoneyCell,
  SkuCell,
  TransactionLineTable,
  type LineColumn,
} from '../components/TransactionLineTable.tsx';
import { apiFetch } from '../lib/api.ts';
import { dateOnly, money, num, statusClass, statusLabel } from '../lib/format.ts';

interface Line {
  lineNo: number;
  productCode: string | null;
  description: string | null;
  qty: number;
  unitPrice: number;
  discountRate: number;
  subTotal: number;
  location: string | null;
}

interface Detail {
  id: number;
  csNo: string;
  csDate: string;
  customerNo: string | null;
  customerName: string | null;
  salesman: string | null;
  currency: string;
  value: number;
  status: string;
  lines: Line[];
}

export function CashSaleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['cash-sale', id],
    queryFn: () =>
      apiFetch<Detail>(`/api/dashboard/cash-sales/${encodeURIComponent(id ?? '')}`),
    enabled: !!id,
  });

  const productLines = (data?.lines ?? []).filter((l) => l.productCode);
  const columns: LineColumn<Line>[] = [
    { label: '#', className: 'mono', render: (l) => l.lineNo },
    { label: 'SKU', className: 'mono', render: (l) => <SkuCell code={l.productCode} /> },
    { label: 'Description', render: (l) => l.description || '—' },
    { label: 'Loc', className: 'mono', render: (l) => l.location || '—' },
    { label: 'Qty', className: 'num', render: (l) => num(l.qty) },
    { label: 'Unit price', className: 'num', render: (l) => <MoneyCell amount={l.unitPrice} currency={data?.currency ?? 'MYR'} /> },
    { label: 'Subtotal', className: 'num', render: (l) => <MoneyCell amount={l.subTotal} currency={data?.currency ?? 'MYR'} /> },
  ];

  return (
    <>
      <Link to="/cash-sales" className="back-link">← All cash sales</Link>
      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}
      {data && (
        <>
          <header className="main-header">
            <div>
              <h1 className="main-title">CS {data.csNo}</h1>
              <span className="main-subtitle mono">{dateOnly(data.csDate)}</span>
            </div>
            <span className={`badge ${statusClass(data.status)}`}>{statusLabel(data.status)}</span>
          </header>
          <section className="panel">
            <header className="panel-header"><h2 className="panel-title">Header</h2></header>
            <dl className="detail-grid">
              <dt>CS no</dt><dd className="mono">{data.csNo}</dd>
              <dt>Date</dt><dd>{dateOnly(data.csDate)}</dd>
              <dt>Customer</dt>
              <dd>
                {data.customerNo ? (
                  <Link to={`/customers/${encodeURIComponent(data.customerNo)}`}>
                    {data.customerName ?? data.customerNo}
                  </Link>
                ) : '—'}
              </dd>
              <dt>Salesman</dt><dd>{data.salesman || '—'}</dd>
              <dt>Value</dt><dd>{money(data.value, data.currency)}</dd>
            </dl>
          </section>
          <section className="panel">
            <header className="panel-header">
              <h2 className="panel-title">Line items</h2>
              <span className="panel-subtitle">{productLines.length} item{productLines.length === 1 ? '' : 's'}</span>
            </header>
            <TransactionLineTable lines={productLines} columns={columns} />
          </section>
        </>
      )}
    </>
  );
}
